// MarketStripe — a vertical color accent bar sitting on the left edge of a
// facility card. 3px wide, full height of the row (`align-self: stretch`).
//
// 2026-09-10: the colour comes from `public.markets` through `shared/markets.ts`
// instead of a six-key vocabulary written down in the design tokens. That
// vocabulary matched the region string by SUBSTRING, and the result was worse
// than a miss:
//
//   * three of the eleven metros resolved to a distinct colour;
//   * Colorado Springs silently borrowed Denver's, because both contain
//     "colorado";
//   * seven fell through to the neutral stripe — including all four Texas
//     markets, which is 79 of the 209 live buildings;
//   * and three of its six keys could never be reached from any canonical
//     market name at all.
//
// Worst of it: the Plano building that had been set to a Colorado market by
// mistake rendered a CONFIDENT Colorado stripe, while every one of its real
// Dallas–Fort Worth neighbours rendered grey. The screen argued that the wrong
// answer was the reliable one.
//
// An exact market name or the neutral colour. Nothing in between.
//
// ⛔ Both colours go to CSS as custom properties and tcs.css decides which one
// applies. Asking JS which theme is on would break inside <ForceLight>, which
// pins a subtree to the light channels through CSS inheritance alone.

import { useMarkets, marketColorPair } from '../markets';

export interface MarketStripeProps {
  /** The canonical market name, e.g. "Dallas-Fort Worth Metro". */
  region?: string | null;
  /** Default 3 (px). */
  width?: number;
  className?: string;
}

export function MarketStripe({ region, width = 3, className }: MarketStripeProps) {
  const { markets } = useMarkets();
  const { base, dark } = marketColorPair(markets, region);
  return (
    <span
      aria-hidden="true"
      className={`market-stripe block self-stretch rounded-[3px] ${className ?? ''}`}
      style={
        {
          width,
          '--mkt-dot': base,
          '--mkt-dot-dark': dark,
        } as React.CSSProperties
      }
    />
  );
}
